import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {computeIsGlobalNamespace} from '../../../helpers/models/namespaceHelpers';
import {AppAction} from '../../appActions';
import {DataGroupsActionsEnum} from '../../data/dataGroupsActions';
import {AnalyticsPanelActionsEnum} from './analyticsPanelActions';
import {AnalyticsPanelState} from './analyticsPanelState';
import {analyticsPanelDetailsReducer} from './details/analyticsPanelDetailsReducer';
import {analyticsPanelExportsReducer} from './exports/analyticsPanelExportsReducer';
import {analyticsPanelFiltersReducer} from './filters/analyticsPanelFiltersReducer';
import {analyticsPanelParametersReducer} from './parameters/analyticsParametersReducer';
import {analyticsPanelReportReducer} from './report/analyticsPanelReportReducer';
import {analyticsPanelViewsReducer} from './views/analyticsPanelViewsReducer';
import {makeAnalyticsPanelViewsState} from './views/analyticsPanelViewsState';

/*
 * Analytics panel.
 */

export function analyticsPanelReducer(state: AnalyticsPanelState, action: AppAction): AnalyticsPanelState {
  let newState = state;

  // Filters.
  const newFilters = analyticsPanelFiltersReducer(state.filters, action);
  if (newFilters !== state.filters) {
    newState = {
      ...newState,
      filters: newFilters,
    };
  }

  // Views.
  const newViews = analyticsPanelViewsReducer(state.views, action);
  if (newViews !== state.views) {
    newState = {
      ...newState,
      views: newViews,
    };
  }

  // Report.
  const newReport = analyticsPanelReportReducer(state.report, action);
  if (newReport !== state.report) {
    newState = {
      ...newState,
      report: newReport,
    };
  }

  // Details.
  const newDetails = analyticsPanelDetailsReducer(state.details, action);
  if (newDetails !== state.details) {
    newState = {
      ...newState,
      details: newDetails,
    };
  }

  // Exports.
  const newExports = analyticsPanelExportsReducer(state.exports, action);
  if (newExports !== state.exports) {
    newState = {
      ...newState,
      exports: newExports,
    };
  }

  // Parameters.
  const newParameters = analyticsPanelParametersReducer(state.parameters, action);
  if (newParameters !== state.parameters) {
    newState = {
      ...newState,
      parameters: newParameters,
    };
  }

  switch (action.type) {
    case AnalyticsPanelActionsEnum.LOADED:
      return {
        ...state,
        status: AsyncStatusesEnum.LOADED,
        report: {
          ...state.report,
          type: action.payload.reportType || state.report.type,
        },
      };

    case AnalyticsPanelActionsEnum.SET_SELECTED_NAMESPACE:
      return {
        ...state,
        report: {
          ...state.report,
          // Reset jobs when switching namespace
          jobs: [],
        },
        // Switching the namespace means we need to reload all the views,
        // so set the status back to loading.
        status: AsyncStatusesEnum.LOADING,
        filters: {
          ...state.filters,
          // clear the teams filter when switching out of the global namespace since
          // it is hidden everywhere else
          teams: computeIsGlobalNamespace(action.payload.namespace) ? state.filters.teams : undefined,
        },
        // Views are per-namespace, so clear the views when switching namespaces
        views: makeAnalyticsPanelViewsState(),
      };

    default: {
      // Only for consistency with other reducers that use dataCascade.updatedResources. Analytics panel actions don't
      // currently dispatch DATA_UPDATED actions with analytics event versions.
      if (action.type === DataGroupsActionsEnum.DATA_UPDATED && action.payload.shouldNotCascade) {
        return state;
      }

      // Otherwise, return newState.
      return newState;
    }
  }
}
